import { useEffect, useRef } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ClientsBanner from "@/components/feature/ClientsBanner";

const services = [
  {
    icon: "ri-tools-line",
    title: "Contratista y Subcontratista",
    desc: "Estructuras metálicas con los más altos estándares de calidad y seguridad industrial.",
  },
  {
    icon: "ri-building-2-line",
    title: "Obras Civiles",
    desc: "Ejecución de proyectos de construcción civil con experiencia y equipamiento de primer nivel.",
  },
  {
    icon: "ri-truck-line",
    title: "Arriendo de Maquinarias",
    desc: "Grúas plumas y maquinaria pesada disponible para proyectos de cualquier envergadura.",
  },
  {
    icon: "ri-settings-3-line",
    title: "Mantención Mecánica",
    desc: "Servicio de mantención mecánica y electromagnética para equipos industriales.",
  },
  {
    icon: "ri-store-2-line",
    title: "Compra y Venta de Repuestos",
    desc: "Amplio stock de repuestos industriales para mantener tus equipos operativos.",
  },
  {
    icon: "ri-roadster-line",
    title: "Transporte de Carga",
    desc: "Transporte de carga por carretera (hormigón) con flota propia y operadores certificados.",
  },
];

const stats = [
  { value: "24+", label: "Años de experiencia" },
  { value: "500+", label: "Proyectos completados" },
  { value: "100%", label: "Compromiso con la calidad" },
];

export default function Home() {
  const sectionRefs = useRef<(HTMLDivElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("opacity-100", "translate-y-0");
            entry.target.classList.remove("opacity-0", "translate-y-8");
          }
        });
      },
      { threshold: 0.1 }
    );
    sectionRefs.current.forEach((ref) => {
      if (ref) observer.observe(ref);
    });
    return () => observer.disconnect();
  }, []);

  const addRef = (el: HTMLDivElement | null, index: number) => {
    sectionRefs.current[index] = el;
  };

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <section className="relative h-[90vh] min-h-[600px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=heavy%20industrial%20crane%20machinery%20construction%20site%20Chile%20dramatic%20sky%20steel%20structure%20professional%20photography%20wide%20angle%20golden%20hour%20lighting%20industrial%20workers%20safety%20equipment%20large%20scale%20project&width=1600&height=900&seq=hero-juarez-01&orientation=landscape"
            alt="Charles Juárez maquinaria industrial"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d1b26]/85 via-[#0d1b26]/60 to-transparent" />
        </div>

        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 flex flex-col items-start gap-6">
          <div
            ref={(el) => addRef(el, 0)}
            className="opacity-0 translate-y-8 transition-all duration-700"
          >
            <span className="inline-block bg-orange-500 text-white text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              Desde 2001
            </span>
            <h1 className="text-5xl md:text-7xl font-extrabold text-white leading-tight max-w-2xl">
              Quiénes<br />
              <span className="text-orange-400">Somos</span>
            </h1>
            <p className="mt-5 text-gray-200 text-lg md:text-xl max-w-xl leading-relaxed">
              Empresa líder en arriendo de grúas plumas y maquinaria pesada en la Región de Coquimbo.
            </p>
            <a
              href="#historia"
              className="whitespace-nowrap mt-8 inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3.5 rounded-full transition-all duration-200 cursor-pointer"
            >
              Conoce nuestra historia
              <i className="ri-arrow-down-line" />
            </a>
          </div>
        </div>

        {/* Stats bar */}
        <div className="absolute bottom-0 left-0 w-full bg-white/10 backdrop-blur-sm border-t border-white/20">
          <div className="max-w-7xl mx-auto px-6 py-5 grid grid-cols-3 gap-4">
            {stats.map((s) => (
              <div key={s.label} className="text-center">
                <p className="text-orange-400 text-2xl md:text-3xl font-extrabold">{s.value}</p>
                <p className="text-white/80 text-xs md:text-sm mt-0.5">{s.label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Historia */}
      <section id="historia" className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          <div
            ref={(el) => addRef(el, 1)}
            className="opacity-0 translate-y-8 transition-all duration-700 delay-100"
          >
            <div className="relative rounded-2xl overflow-hidden h-[480px]">
              <img
                src="https://storage.readdy-site.link/project_files/5397651f-fa4a-4daf-a2cb-668ede89c5b0/0f61e599-58b5-43f6-ab1d-dbc5bc3ef41d_1f1cf33f-596d-470f-aa91-4a5cbe49b39b.jpg?v=16162591e64aef664244f64103fcc2a1"
                alt="Historia Charles Juárez"
                className="w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0d1b26]/60 to-transparent" />
              <div className="absolute bottom-6 left-6 right-6">
                <span className="bg-orange-500 text-white text-xs font-bold px-3 py-1.5 rounded-full">
                  Fundada en 2001
                </span>
              </div>
            </div>
          </div>

          <div
            ref={(el) => addRef(el, 2)}
            className="opacity-0 translate-y-8 transition-all duration-700 delay-200"
          >
            <span className="inline-block bg-orange-50 text-orange-600 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              Nuestra Historia
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#1c2b38] leading-tight mb-6">
              Charles Juárez,<br />
              <span className="text-orange-500">a su servicio</span>
            </h2>
            <p className="text-gray-600 text-base md:text-lg leading-relaxed mb-8">
              Nuestra empresa nace en el año 2001, en sus inicios dedicada principalmente al área maestranza, donde el principal recurso siempre ha sido el capital humano. Con el pasar de los años se fue especializando poco a poco en arriendo de grúas plumas y maquinaria, siendo hoy una de las empresas con mayor prestigio en el arriendo de maquinaria local.
            </p>

            {/* Timeline */}
            <div className="flex flex-col gap-5 border-l-2 border-orange-200 pl-6">
              {[
                { year: "2001", text: "Fundación de la empresa, enfocada en maestranza." },
                { year: "2008", text: "Expansión hacia el arriendo de grúas plumas." },
                { year: "2015", text: "Consolidación como referente regional en maquinaria." },
                { year: "2025", text: "Más de 24 años de trayectoria y crecimiento continuo." },
              ].map((item) => (
                <div key={item.year} className="relative">
                  <span className="absolute -left-8 top-1 w-4 h-4 rounded-full bg-orange-500 border-2 border-white" />
                  <p className="text-orange-500 text-xs font-bold uppercase tracking-widest">{item.year}</p>
                  <p className="text-gray-600 text-sm mt-0.5">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Giro Comercial */}
      <section className="py-24 bg-[#f7f8fa]">
        <div className="max-w-7xl mx-auto px-6">
          <div
            ref={(el) => addRef(el, 3)}
            className="opacity-0 translate-y-8 transition-all duration-700 text-center mb-14"
          >
            <span className="inline-block bg-orange-50 text-orange-600 text-xs font-bold uppercase tracking-widest px-4 py-1.5 rounded-full mb-4">
              Giro Comercial
            </span>
            <h2 className="text-4xl md:text-5xl font-extrabold text-[#1c2b38]">
              Nuestras Actividades
            </h2>
            <p className="text-gray-500 mt-4 max-w-xl mx-auto text-base">
              Ofrecemos una amplia gama de servicios industriales para satisfacer las necesidades de nuestros clientes.
            </p>
          </div>

          <div
            ref={(el) => addRef(el, 4)}
            className="opacity-0 translate-y-8 transition-all duration-700 delay-100 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {services.map((s) => (
              <div
                key={s.title}
                className="bg-white rounded-2xl p-7 border border-gray-100 hover:border-orange-200 hover:-translate-y-1 transition-all duration-300 cursor-default group"
              >
                <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-orange-50 group-hover:bg-orange-500 transition-colors duration-300 mb-5">
                  <i className={`${s.icon} text-xl text-orange-500 group-hover:text-white transition-colors duration-300`} />
                </div>
                <h3 className="text-[#1c2b38] font-bold text-base mb-2">{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Clients Banner */}
      <ClientsBanner />

      {/* CTA Banner */}
      <section
        ref={(el) => addRef(el, 5)}
        className="opacity-0 translate-y-8 transition-all duration-700 relative py-20 overflow-hidden"
      >
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=industrial%20machinery%20crane%20aerial%20view%20construction%20site%20Chile%20dramatic%20wide%20angle%20heavy%20equipment%20workers%20steel%20beams%20professional%20industrial%20photography%20warm%20tones&width=1600&height=500&seq=cta-juarez-03&orientation=landscape"
            alt="CTA background"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#0d1b26]/80" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-5xl font-extrabold text-white mb-4">
            ¿Necesitas maquinaria para tu proyecto?
          </h2>
          <p className="text-gray-300 text-lg mb-8">
            Contáctanos hoy y te asesoramos sin compromiso.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/arriendo-maquinarias"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-semibold px-8 py-3.5 rounded-full transition-all duration-200 cursor-pointer"
            >
              Ver Maquinarias
              <i className="ri-arrow-right-line" />
            </a>
            <a
              href="/contacto"
              className="whitespace-nowrap inline-flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-8 py-3.5 rounded-full border border-white/30 transition-all duration-200 cursor-pointer"
            >
              Contáctanos
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
